<template>
  <div>
    <div :class="$style.grid">
      <div :class="$style.row">
        <div>
          <HeadingExtraSmall>
            {{ directionColumnName }}
          </HeadingExtraSmall>
        </div>
        <div>
          <HeadingExtraSmall>
            JumpCloud attribute
          </HeadingExtraSmall>
        </div>
        <div />
        <div>
          <HeadingExtraSmall>
            {{ directoryName }} attribute
          </HeadingExtraSmall>
        </div>
      </div>

      <hr :class="$style.divider">

      <div
        v-for="attribute in attributes"
        :key="attribute.jc"
        :class="$style.row"
        :name="`${attribute.key}_row`"
      >
        <div
          :class="{
            [$style.direction]: Object.isFrozen(attribute),
          }"
        >
          <span v-if="isJumpCloudAttribute(attribute)">
            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" :class="$style.lockIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M4.4 5.6v-.8a3.2 3.2 0 016.4 0v.8H4.4zm-1.6.276V4.8a4.8 4.8 0 119.6 0v1.076c.481.31.8.85.8 1.465V10.4a5.6 5.6 0 11-11.2 0V7.341c0-.615.319-1.155.8-1.465zM3.741 7.2a.141.141 0 00-.141.141V10.4a4 4 0 008 0V7.341a.141.141 0 00-.141-.141H3.74zM7.6 8.8a1.6 1.6 0 110 3.2 1.6 1.6 0 010-3.2z" fill-rule="evenodd"/></svg>
            JumpCloud owned
          </span>
          <!-- Using :value instad of v-model to prevent
            do not mutate vuex store state outside mutation handlers-->
          <Select
            v-else
            :name="`${attribute.key}_direction`"
            :options="getDirectionOptions(attribute.key)"
            :placeholder="'JumpCloud to import or export?'"
            :value="attribute.direction"
            @input="(direction) => handleChange(attribute.key, direction)"
          />
        </div>
        <div>
          <Select
            v-if="attribute.attrNames.directory == userPrincipalName"
            :name="`${attribute.key}_jcAttribute`"
            :options="getUserPrincipalNameOptions"
            :placeholder="'Email'"
            :value="attribute.value ? ATTRIBUTE_ALT_EMAIL : ATTRIBUTE_EMAIL"
            @input="(value) =>
              handleChange(attribute.key, value === ATTRIBUTE_ALT_EMAIL ?
                EXPORT_ATTRIBUTE_DIRECTION :EXCLUDE_ATTRIBUTE_DIRECTION)"
          />
          <InputText
            v-else
            isDisabled
            :name="`${attribute.key}_jcAttribute`"
            :value="attribute.attrNames.jc"
          />
        </div>
        <div :class="$style.dirText">
          <span>{{ ShortDirectionText[attribute.direction] || 'to' }}</span>
        </div>
        <div>
          <InputText
            isDisabled
            :name="`${attribute.key}_directoryAttribute`"
            :value="attribute.attrNames.directory"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DirectoryAttributeDirectionConsts, DirectoryNameConsts, DirectoryTypeConsts,
  DirectoryUserPrincipalNameFromAlternateEmail, jumpcloudUserAttributes,
} from 'client/util/Constants';
import { HeadingExtraSmall, InputText, Select } from '@jumpcloud/ui-components';
import { mapActions, mapState } from 'vuex';
import { o365Fields } from 'client/util/Constants/DirectoryConstants';
import { uniqueId } from 'lodash';

export default {
  name: 'AttributesMappingGrid',

  components: {
    HeadingExtraSmall,
    InputText,
    Select,
  },

  props: {
    attributeNames: {
      type: Array,
      required: true,
    },

    directionColumnName: {
      type: String,
      required: true,
    },

    directoryName: {
      type: String,
      required: true,
    },

    storeName: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    ShortDirectionText: {
      export: 'to',
      import: 'from',
    },
  }),

  computed: {
    ...mapState({
      attributes() {
        const { storeName, attributeNames } = this;
        const { exportAttributes } = this.$store.state[storeName];
        const topAttributeNames = [];
        const regularAttributeNames = [];
        attributeNames.forEach((attr) => {
          if (this.isJumpCloudAttribute(exportAttributes[attr])) {
            topAttributeNames.push(exportAttributes[attr]);
          } else {
            regularAttributeNames.push(exportAttributes[attr]);
          }
        });
        const sortedAttributeNames = regularAttributeNames
          .sort((a, b) => (a.attrNames.jc > b.attrNames.jc ? 1 : -1));
        return [...topAttributeNames, ...sortedAttributeNames];
      },
    }),
    getUserPrincipalNameOptions() {
      return [{
        name: DirectoryUserPrincipalNameFromAlternateEmail.companyEmail.name,
        value: DirectoryUserPrincipalNameFromAlternateEmail.companyEmail.value,
      },
      {
        name: DirectoryUserPrincipalNameFromAlternateEmail.alternateEmail.name,
        value: DirectoryUserPrincipalNameFromAlternateEmail.alternateEmail.value,
      }];
    },
    userPrincipalName() {
      return o365Fields.userPrincipalName;
    },
  },

  created() {
    const action = mapActions(this.$options.propsData.storeName, [
      'setExportAttribute',
    ]);
    this.setExportAttribute = action.setExportAttribute;
    this.EXCLUDE_ATTRIBUTE_DIRECTION = DirectoryAttributeDirectionConsts.exclude.value;
    this.EXPORT_ATTRIBUTE_DIRECTION = DirectoryAttributeDirectionConsts.export.value;
    this.ATTRIBUTE_EMAIL = DirectoryUserPrincipalNameFromAlternateEmail.companyEmail.value;
    this.ATTRIBUTE_ALT_EMAIL = DirectoryUserPrincipalNameFromAlternateEmail.alternateEmail.value;
  },

  methods: {
    getDirectionOptions(key) {
      return this.isImportEnabled(key)
        ? [{
          name: DirectoryAttributeDirectionConsts.export.name,
          value: DirectoryAttributeDirectionConsts.export.value,
        }, {
          name: DirectoryAttributeDirectionConsts.import.name,
          value: DirectoryAttributeDirectionConsts.import.value,
        }, {
          name: DirectoryAttributeDirectionConsts.exclude.name,
          value: DirectoryAttributeDirectionConsts.exclude.value,
        }]
        : [{
          name: DirectoryAttributeDirectionConsts.export.name,
          value: DirectoryAttributeDirectionConsts.export.value,
        }, {
          name: DirectoryAttributeDirectionConsts.exclude.name,
          value: DirectoryAttributeDirectionConsts.exclude.value,
        }];
    },

    handleChange(key, direction) {
      if (direction === this.EXCLUDE_ATTRIBUTE_DIRECTION) {
        this.setExportAttribute({ key, value: false, direction });
      } else {
        this.setExportAttribute({ key, value: true, direction });
      }
    },

    isImportEnabled(key) {
      return (this.directoryName === DirectoryNameConsts[DirectoryTypeConsts.gSuite]
        && key !== 'user_manager')
      || (this.directoryName === DirectoryNameConsts[DirectoryTypeConsts.gSuite]
        && key === 'user_manager'
        && this.$hasFeatureFlag(this.$featureFlags.importManagerAttributeFromGoogle))
      || (this.directoryName === DirectoryNameConsts[DirectoryTypeConsts.office365]
        && this.$hasFeatureFlag(this.$featureFlags.importAdditionalAttributesFromM365));
    },

    isJumpCloudAttribute(attribute) {
      return jumpcloudUserAttributes.includes(attribute.key);
    },

    unique: () => uniqueId(),
  },
};
</script>

<style module>
@import "../../components/FormValidation/css/formFields.css";
@import "./AttributesMappingGrid.css";

.lockIcon {
  fill: currentColor;
  height: 14px;
  margin: 0 5px 0 2px;
  position: relative;
  width: 14px;
}
</style>
